import { useEffect, useMemo } from "react";

import { startCase } from "lodash";
import {
  Controller,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormReturn,
  useWatch,
} from "react-hook-form";

import { Button, clsx, Form, Input, Select, Text } from "@alpacahq/ui/old";

import { INVESTMENT_OBJECTIVE_OPTIONS } from "~/v2/constants/investmentObjective";
import type { UseCountryReturn } from "../../../../../api/hooks/useCountry";
import {
  BUSINESS_TYPES,
  ENTITY_TYPES,
  FUNDING_SOURCES_OPTIONS,
  INTENDED_ACCOUNT_USES_OPTIONS,
  NOT_ALLOWED_LEGAL_ENTITY_TAX_ID_TYPES,
  NOT_SPECIFIC_TAX_ID_TYPE,
  RANGE_OPTIONS,
  USA,
} from "../../constants";
import { EntityCountryField } from "../../EntityCountryField";
import { StateInput } from "../../StateInput";
import {
  formatEIN,
  getMinMaxFromRangeOptions,
  getRangeKeyFromValues,
  isAllAsterisks,
} from "../../util";
import { YesNoCheckbox } from "../../YesNoCheckbox";
import type { BusinessInfo } from "./schema";

export interface BusinessInfoFormProps {
  form: UseFormReturn<BusinessInfo>;
  isSubmitDisabled: boolean;
  onSubmit: SubmitHandler<BusinessInfo>;
  onSubmitError: SubmitErrorHandler<BusinessInfo>;
  onBackClicked: () => void;
  country: UseCountryReturn;
  isBackHidden?: boolean;
  isTaxIDRequired: boolean;
}

export const BusinessInfoForm = ({
  form,
  isSubmitDisabled,
  onSubmit,
  onSubmitError,
  country,
  onBackClicked,
  isBackHidden,
  isTaxIDRequired,
}: BusinessInfoFormProps) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = form;

  const isExemptFromTaxUnder501a = useWatch({
    control,
    name: "is_exempt_from_tax_under_501a",
    defaultValue: false,
  });
  const isParticipatingFATCA = useWatch({
    control,
    name: "is_participating_fatca",
    defaultValue: false,
  });

  const industry = useWatch({
    control,
    name: "type_of_business",
  });

  const countryOfIncorporation = useWatch({
    control,
    name: "country_of_incorporation",
  });

  const countryOfTaxResidence = useWatch({
    control,
    name: "country_of_tax_residence",
  });

  const businessAddressCountry = useWatch({
    control,
    name: "country",
  });

  const isIndustryDescriptionRequired = useMemo(() => {
    return industry === "other";
  }, [industry]);

  const isStateOfIncorporationRequired = useMemo(() => {
    return countryOfIncorporation === USA;
  }, [countryOfIncorporation]);

  const isUSABusiness = useMemo(() => {
    return businessAddressCountry === USA;
  }, [businessAddressCountry]);

  useEffect(() => {
    if (!isIndustryDescriptionRequired) {
      setValue("type_of_business_other_free_text", "");
      clearErrors("type_of_business_other_free_text");
    }
  }, [isIndustryDescriptionRequired, setValue, clearErrors]);

  const taxIDTypeOptions = useMemo(() => {
    const countryOfTaxResidenceAlpha3 =
      country.nameToAlpha3(countryOfTaxResidence) || "";

    const taxIDTypes = country.infos[countryOfTaxResidenceAlpha3]
      ?.tax_id_types || [NOT_SPECIFIC_TAX_ID_TYPE];

    return taxIDTypes.filter(
      ({ id_type }) => !NOT_ALLOWED_LEGAL_ENTITY_TAX_ID_TYPES.includes(id_type),
    );
  }, [countryOfTaxResidence, country]);

  const taxIDType = useWatch({
    control,
    name: "tax_id_type",
  });
  const taxIDRegex = new RegExp(
    taxIDTypeOptions.find(
      (tax_id) =>
        tax_id.id_type === taxIDType || tax_id.id_type === "NOT_SPECIFIED",
    )?.regexp || "^.*$",
  );

  const liquidNetWorthMin = useWatch({ control, name: "liquid_net_worth_min" });
  const liquidNetWorthMax = useWatch({ control, name: "liquid_net_worth_max" });
  const totalNetWorthMin = useWatch({ control, name: "total_net_worth_min" });
  const totalNetWorthMax = useWatch({ control, name: "total_net_worth_max" });
  const annualIncomeMin = useWatch({ control, name: "annual_income_min" });
  const annualIncomeMax = useWatch({ control, name: "annual_income_max" });

  const financialValues = useMemo(
    () => ({
      liquidNetWorth: {
        min: liquidNetWorthMin,
        max: liquidNetWorthMax,
      },
      totalNetWorth: {
        min: totalNetWorthMin,
        max: totalNetWorthMax,
      },
      annualIncome: {
        min: annualIncomeMin,
        max: annualIncomeMax,
      },
    }),
    [
      liquidNetWorthMin,
      liquidNetWorthMax,
      totalNetWorthMin,
      totalNetWorthMax,
      annualIncomeMin,
      annualIncomeMax,
    ],
  );

  const onChangeRange =
    ({
      minField,
      maxField,
    }: {
      minField:
        | "annual_income_min"
        | "liquid_net_worth_min"
        | "total_net_worth_min";
      maxField:
        | "annual_income_max"
        | "liquid_net_worth_max"
        | "total_net_worth_max";
    }) =>
    (value: string) => {
      const { min, max } = getMinMaxFromRangeOptions(value);
      setValue(minField, min);
      setValue(maxField, max);
    };

  useEffect(() => {
    if (!isStateOfIncorporationRequired) {
      setValue("state_of_incorporation", "");
    }
    if (!isIndustryDescriptionRequired) {
      setValue("type_of_business_other_free_text", "");
    }
  }, [isStateOfIncorporationRequired, isIndustryDescriptionRequired, setValue]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: businessAddressCountry is the trigger: the state field is cleared when the business address country changes. The body writes rather than reads it.
  useEffect(() => {
    setValue("state", "");
  }, [businessAddressCountry, setValue]);

  return (
    <Form formMethods={form} onSubmit={onSubmit} onSubmitError={onSubmitError}>
      <h2 className="font-medium text-lg">Business Information</h2>

      <Form.TextInput
        type="text"
        label="Legal Business Name"
        name="legal_name"
        placeholder="Legal Business Name"
        className="bg-white"
      />

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <EntityCountryField<BusinessInfo>
          isRequired={false}
          isInvalid={Boolean(errors.country_of_incorporation)}
          errorMsg={errors.country_of_incorporation?.message}
          label="Country of Incorporation"
          name="country_of_incorporation"
          control={form.control}
          countries={country.allowedCountries}
          placeholder="Country of Incorporation"
        />

        <div className="flex flex-col gap-2">
          <label className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100">
            State of Incorporation
          </label>
          <StateInput<BusinessInfo>
            name="state_of_incorporation"
            control={control}
            placeholder="State of Incorporation (U.S. Only)"
            disabled={!isStateOfIncorporationRequired}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <Form.SelectInput
          label="Business Type"
          name="entity_type"
          options={ENTITY_TYPES.map((value) => ({
            label: startCase(value),
            value,
          }))}
          placeholder="Select Business Type"
        />
        <Form.DatePickerInput
          label="Date of Incorporation"
          name="date_of_incorporation"
          placeholder="MM/DD/YYYY"
        />
      </div>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <Form.TextInput
          type="text"
          label="Business Email"
          name="email"
          placeholder="Business Email"
          className="bg-white"
        />
        <Form.TextInput
          type="text"
          label="Business Phone Number"
          name="phone_number"
          placeholder="Business Phone Number"
          className="bg-white"
        />
      </div>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <Form.TextInput
          type="text"
          label="Business Address Line 1"
          name="street_address"
          placeholder="Street Address"
          autoComplete="address-line1"
          className="bg-white"
        />
        <Form.TextInput
          type="text"
          label="Address Line 2"
          name="unit"
          placeholder="Suite, Unit, Building, Floor, etc."
          autoComplete="address-line2"
          className="bg-white"
        />
      </div>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <Form.TextInput
          type="text"
          label="City"
          name="city"
          placeholder="City"
          autoComplete="address-level2"
          className="bg-white"
        />
        {isUSABusiness ? (
          <div className="flex flex-col gap-2">
            <label className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100">
              State
            </label>
            <StateInput<BusinessInfo>
              name="state"
              control={control}
              isInvalid={Boolean(errors.state)}
              errorMsg={errors.state?.message}
            />
          </div>
        ) : (
          <Form.TextInput
            type="text"
            label="State"
            name="state"
            placeholder="State"
            autoComplete="address-level1"
            className="bg-white"
          />
        )}
      </div>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <Form.TextInput
          type="text"
          label="Postal Code"
          name="postal_code"
          placeholder="Postal Code"
          autoComplete="postal-code"
          className="bg-white"
        />
        <EntityCountryField<BusinessInfo>
          isRequired={false}
          isInvalid={Boolean(errors.country)}
          errorMsg={errors.country?.message}
          label="Country"
          name="country"
          control={form.control}
          countries={country.allowedCountries}
          placeholder="Country"
        />
      </div>

      <Form.TextInput
        type="text"
        label="Business Registration Number"
        name="business_registration_number"
        placeholder="Business Registration Number"
        className="bg-white"
      />

      <h2 className="pt-2 font-medium text-lg">Business Tax Information</h2>

      <EntityCountryField<BusinessInfo>
        isRequired={false}
        isInvalid={Boolean(errors.country_of_tax_residence)}
        errorMsg={errors.country_of_tax_residence?.message}
        label="Tax Jurisdiction"
        name="country_of_tax_residence"
        control={form.control}
        countries={country.allowedCountries}
        placeholder="Country business files taxes"
      />

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <Form.SelectInput
          label="Business Tax ID Type"
          name="tax_id_type"
          options={taxIDTypeOptions.map(({ label, id_type }) => ({
            label,
            value: id_type,
          }))}
          placeholder="Select tax ID type"
        />
        <Controller
          name={"tax_id"}
          control={control}
          rules={{ required: isTaxIDRequired, pattern: taxIDRegex }}
          render={({ field: { onChange, ...field } }) => (
            <div className="flex flex-col gap-2">
              <label className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100">
                Tax ID
              </label>
              <Input
                {...field}
                onChange={(event) => {
                  const taxID =
                    taxIDType === "USA_EIN"
                      ? formatEIN(event.target.value)
                      : event.target.value;

                  const isTaxIDCheckRequired =
                    isTaxIDRequired || !isAllAsterisks(taxID ?? "");

                  const isTaxIDInvalid = isTaxIDCheckRequired
                    ? !taxIDRegex.test(taxID)
                    : false;

                  if (isTaxIDInvalid) {
                    setError("tax_id", { message: "Invalid Tax ID" });
                  } else {
                    clearErrors("tax_id");
                  }
                  onChange(taxID);
                }}
                onBlur={(event) => {
                  const taxID = event.currentTarget.value;

                  const isTaxIDCheckRequired =
                    isTaxIDRequired || !isAllAsterisks(taxID);

                  if (
                    isTaxIDCheckRequired &&
                    !taxIDRegex.test(event.currentTarget.value)
                  ) {
                    setError("tax_id", { message: "Invalid Tax ID" });
                  }
                }}
                className="bg-white"
                placeholder="Tax ID number"
              />
              {errors.tax_id?.message && (
                <Text className="truncate text-rose-500 text-xs">
                  {errors.tax_id?.message}
                </Text>
              )}
            </div>
          )}
        />
      </div>

      <YesNoCheckbox
        label="Is this business exempt from tax under section 501(a) of the Internal Revenue Code?"
        setValue={(isChecked) =>
          setValue("is_exempt_from_tax_under_501a", isChecked)
        }
        isChecked={isExemptFromTaxUnder501a}
      />

      <YesNoCheckbox
        label="Is the business a participating FATCA institution?"
        setValue={(isChecked) => setValue("is_participating_fatca", isChecked)}
        isChecked={isParticipatingFATCA}
      />

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <Form.SelectInput
          label="Industry"
          name="type_of_business"
          options={BUSINESS_TYPES.map((value) => ({
            label: startCase(value),
            value,
          }))}
          placeholder="Select Industry"
        />
        {isIndustryDescriptionRequired && (
          <Form.TextInput
            type="text"
            label="Other Industry"
            name="type_of_business_other_free_text"
            placeholder="Please specify your industry"
            className="bg-white"
          />
        )}
      </div>

      <Form.SelectInput
        label="Intended Use of Account"
        name="intended_use_of_account"
        options={INTENDED_ACCOUNT_USES_OPTIONS}
        placeholder="Select intended use of account"
      />

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label
            className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100"
            htmlFor="annual_business_income"
          >
            Annual Business Income
          </label>
          <Select
            id="annual_business_income"
            multiple={false}
            value={getRangeKeyFromValues(financialValues.annualIncome)}
            onChange={onChangeRange({
              minField: "annual_income_min",
              maxField: "annual_income_max",
            })}
            options={RANGE_OPTIONS}
            placeholder="Select business income"
          />
        </div>

        <Form.SelectInput
          label="Funding Source"
          name="funding_source"
          options={FUNDING_SOURCES_OPTIONS}
          placeholder="Select funding source"
        />
      </div>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label
            className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100"
            htmlFor="liquid_net_worth"
          >
            Business Liquid Net Worth
          </label>
          <Select
            id="liquid_net_worth_min"
            multiple={false}
            value={getRangeKeyFromValues(financialValues.liquidNetWorth)}
            onChange={onChangeRange({
              minField: "liquid_net_worth_min",
              maxField: "liquid_net_worth_max",
            })}
            options={RANGE_OPTIONS}
            placeholder="Select business liquid net worth"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label
            className="block font-medium text-gray-900 text-md leading-6 dark:text-gray-100"
            htmlFor="total_net_worth"
          >
            Business Total Net Worth
          </label>
          <Select
            id="total_net_worth"
            multiple={false}
            value={getRangeKeyFromValues(financialValues.totalNetWorth)}
            onChange={onChangeRange({
              minField: "total_net_worth_min",
              maxField: "total_net_worth_max",
            })}
            options={RANGE_OPTIONS}
            placeholder="Select business total net worth"
          />
        </div>
      </div>

      <Form.SelectInput
        label="Investment Objective"
        name="investment_objective"
        options={INVESTMENT_OBJECTIVE_OPTIONS}
        placeholder="Select investment objective"
      />

      <div
        className={clsx(
          "flex w-full items-center gap-4 pt-9",
          isBackHidden ? "justify-end" : "justify-between",
        )}
      >
        {!isBackHidden && (
          <Button type="button" onClick={onBackClicked} variant="secondary">
            Back
          </Button>
        )}
        <Button type="submit" disabled={isSubmitDisabled}>
          Next
        </Button>
      </div>
    </Form>
  );
};
